<!--
  ══════════════════════════════════════════════════════════════════════════════
  PLANES — rehecho el 2026-08-20 con el vocabulario `.kb-*` de la casa.

  🔴 NO SE TOCÓ NADA DE LÓGICA. Todos los enlaces del componente están intactos:
  los 28 `(click)`, los 4 `[(ngModel)]`, los 10 `*ngFor`, `sortBy`, `clearFilters`,
  la paginación y los permisos. Es un cambio de marcado y de clases.

  Qué cambió y por qué:

  1. **De 130 colores crudos a cero.** Todo sale de un token semántico a través de
     las clases `.kb-*`. Antes había ocho familias de color —azul, cian, gris,
     verde, naranja, morado, rojo, amarillo— sin que ninguna significara nada: el
     mismo botón de ayuda aparecía tres veces en tres colores distintos. Cuando el
     color no significa nada, el usuario deja de leerlo, y entonces tampoco lee el
     rojo cuando aparece un error de verdad.

  2. **Se fue la vista de tarjetas.** No es una preferencia: los listados de esta
     consola van en TABLA por decisión del dueño del producto, y las tarjetas ya
     se habían rechazado explícitamente. Además el mismo listado estaba escrito
     dos veces —tarjetas y tabla— o sea ~300 líneas duplicadas donde cada cambio
     había que hacerlo dos veces.

  3. **El orden pasó a la cabecera de la tabla.** Estaba en una barra de botones
     aparte, arriba, desconectada de la columna que ordenaba. Ahora se toca el
     encabezado, que es donde todo el mundo lo busca. Misma función `sortBy`.

  4. **El estado del plan pasó a ser una insignia.** Antes se leía solo por el
     color de un interruptor. Ahora dice «Activo» o «Inactivo» con palabras: el
     color acompaña, no informa solo.

  ⚠️ `viewMode` quedó en el componente y sin uso. Se deja a propósito: sacarlo es
  tocar lógica, y esto es un cambio de marcado.
  ══════════════════════════════════════════════════════════════════════════════
-->
<div class="kb-page">

  <!-- ══ Encabezado ═════════════════════════════════════════════════════════ -->
  <div class="kb-page-header">
    <div>
      <h1 class="kb-page-title">Planes de financiamiento</h1>
      <p class="kb-page-desc">
        Los planes con los que un cliente puede comprar a crédito: su plazo, sus cuotas y su
        prioridad. Un plan inactivo deja de ofrecerse, pero los créditos que ya se otorgaron con
        él siguen su curso.
      </p>
    </div>
    <div class="kb-acciones-grupo">
      <button type="button" class="kb-btn kb-btn-ghost kb-btn-sm"
              (click)="toggleHelp('dashboard')"
              title="Cómo funciona esta pantalla">
        <i class="fas fa-question-circle" aria-hidden="true"></i>Ayuda
      </button>
      <button type="button" class="kb-btn kb-btn-primary"
              *ngIf="authService.hasPermission('config.planes.create')"
              (click)="crearNuevoPlan()">
        <i class="fas fa-plus" aria-hidden="true"></i>Nuevo plan
      </button>
    </div>
  </div>

  <!-- Ayuda general. Es un aviso informativo, no una caja azul inventada. -->
  <div class="kb-aviso kb-aviso-info" *ngIf="showHelp['dashboard']">
    <i class="fas fa-info-circle mt-0.5" aria-hidden="true"></i>
    <div class="flex-1">
      <p class="kb-aviso-titulo">{{ helpData['dashboard']?.title }}</p>
      <p>{{ helpData['dashboard']?.description }}</p>
      <div class="mt-3" *ngFor="let section of helpData['dashboard']?.sections">
        <p class="font-medium">{{ section.title }}</p>
        <ul class="mt-1 ml-4 list-disc space-y-1">
          <li *ngFor="let item of section.items">
            <b>{{ item.label }}:</b> {{ item.description }}
          </li>
        </ul>
      </div>
      <p class="mt-3" *ngIf="helpData['dashboard']?.tip as tip">
        <b>{{ tip.title }}:</b> {{ tip.text }}
      </p>
    </div>
  </div>

  <!-- ══ Métricas ═══════════════════════════════════════════════════════════ -->
  <div class="kb-stat-grid">
    <div class="kb-stat">
      <div class="kb-stat-fila">
        <span class="kb-stat-icono"><i class="fas fa-layer-group" aria-hidden="true"></i></span>
        <span class="kb-stat-label">Planes</span>
      </div>
      <p class="kb-stat-valor">{{ metrics.totalPlanes }}</p>
      <p class="kb-stat-pie">{{ metrics.nuevosEstesMes }} nuevos este mes</p>
    </div>

    <div class="kb-stat">
      <div class="kb-stat-fila">
        <span class="kb-stat-icono"><i class="fas fa-check-circle" aria-hidden="true"></i></span>
        <span class="kb-stat-label">Activos</span>
      </div>
      <p class="kb-stat-valor">{{ metrics.planesActivos }}</p>
      <p class="kb-stat-pie">{{ metrics.porcentajeActivos }}% del total</p>
    </div>

    <div class="kb-stat">
      <div class="kb-stat-fila">
        <span class="kb-stat-icono"><i class="fas fa-pause-circle" aria-hidden="true"></i></span>
        <span class="kb-stat-label">Inactivos</span>
      </div>
      <p class="kb-stat-valor">{{ metrics.totalPlanes - metrics.planesActivos }}</p>
      <p class="kb-stat-pie">No se ofrecen al cliente</p>
    </div>

    <!-- 📍 `kb-stat-alerta` solo cuando hay algo que atender. Un recuadro que
         siempre está resaltado deja de resaltar. -->
    <div class="kb-stat" [ngClass]="{'kb-stat-alerta': (metrics.proximosVencer || 0) > 0}">
      <div class="kb-stat-fila">
        <span class="kb-stat-icono"><i class="fas fa-hourglass-half" aria-hidden="true"></i></span>
        <span class="kb-stat-label">Próximos a vencer</span>
      </div>
      <p class="kb-stat-valor">{{ metrics.proximosVencer || 0 }}</p>
      <p class="kb-stat-pie">Revisar su vigencia</p>
    </div>
  </div>

  <!-- ══ Filtros ════════════════════════════════════════════════════════════
       🔴 REHECHOS EL 2026-08-21 CONTRA LOS DATOS REALES (57 planes):

       Se fueron dos filtros que no servían:
         · «público / privado» — los 57 planes son PUBLIC. No separaba NADA.
         · «prioridad» — varía en la base, pero el negocio no la usa.

       Y entró el que faltaba: el TIPO de plan (crédito · contado · suscripción),
       que es por donde se busca de verdad y no estaba en ningún lado.

       📍 Los ocho filtros NO están todos puestos de entrada. Arriba quedan los
       tres que se usan siempre —buscar, tipo y estado— y el resto se agrega de
       a uno con «Agregar filtro». Una barra con ocho controles obliga a leer
       ocho para usar uno, y el que no se usa igual ocupa lugar.
       ══════════════════════════════════════════════════════════════════════ -->
  <div class="kb-toolbar">

    <!-- ── Fila 1: lo que está siempre ─────────────────────────────────── -->
    <div class="flex w-full flex-wrap items-end gap-3">

      <div class="kb-buscador min-w-[240px] flex-1">
        <i class="fas fa-search kb-buscador-icono" aria-hidden="true"></i>
        <input type="text" class="kb-input kb-input-icono"
               placeholder="Nombre, código o descripción…"
               [(ngModel)]="filters.searchTerm" (input)="onSearch()">
      </div>

      <!-- Tipo de plan: tres opciones y «todos», con el conteo adentro. El
           número es la mitad del valor: dice de una si vale la pena entrar. -->
      <div class="kb-tabs">
        <button type="button" class="kb-tab" [ngClass]="{'kb-tab-activo': filters.tipo === ''}"
                (click)="filters.tipo = ''; onFilterChange()">
          Todos <span class="ml-1 tabular-nums opacity-60">{{ cuantosDeTipo('') }}</span>
        </button>
        <button type="button" class="kb-tab" [ngClass]="{'kb-tab-activo': filters.tipo === 'CREDITO'}"
                (click)="filters.tipo = 'CREDITO'; onFilterChange()">
          Crédito <span class="ml-1 tabular-nums opacity-60">{{ cuantosDeTipo('CREDITO') }}</span>
        </button>
        <button type="button" class="kb-tab" [ngClass]="{'kb-tab-activo': filters.tipo === 'CONTADO'}"
                (click)="filters.tipo = 'CONTADO'; onFilterChange()">
          Contado <span class="ml-1 tabular-nums opacity-60">{{ cuantosDeTipo('CONTADO') }}</span>
        </button>
        <button type="button" class="kb-tab" [ngClass]="{'kb-tab-activo': filters.tipo === 'SUSCRIPCION'}"
                (click)="filters.tipo = 'SUSCRIPCION'; onFilterChange()">
          Suscripción <span class="ml-1 tabular-nums opacity-60">{{ cuantosDeTipo('SUSCRIPCION') }}</span>
        </button>
      </div>

      <div class="kb-select-envoltorio w-[150px]">
        <select class="kb-select" [(ngModel)]="filters.filterEstado" (change)="onFilterChange()"
                aria-label="Estado">
          <option value="">Estados</option>
          <option value="active">Activos</option>
          <option value="inactive">Inactivos</option>
        </select>
        <i class="fas fa-chevron-down kb-select-flecha" aria-hidden="true"></i>
      </div>

      <!-- Agregar filtro -->
      <div class="relative">
        <button type="button" class="kb-btn kb-btn-outline"
                (click)="mostrarMenuFiltros = !mostrarMenuFiltros"
                [disabled]="filtrosDisponibles.length === 0">
          <i class="fas fa-plus" aria-hidden="true"></i>Agregar filtro
        </button>
        <div *ngIf="mostrarMenuFiltros && filtrosDisponibles.length > 0"
             class="absolute right-0 z-30 mt-2 w-60 rounded-md border border-border bg-popover p-1 shadow-md">
          <button type="button" *ngFor="let f of filtrosDisponibles"
                  (click)="agregarFiltro(f.clave)"
                  class="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm hover:bg-accent">
            <i class="fas {{ f.icono }} w-4 text-primary" aria-hidden="true"></i>{{ f.label }}
          </button>
        </div>
      </div>

      <button type="button" class="kb-btn kb-btn-ghost" (click)="clearFilters()">
        <i class="fas fa-times" aria-hidden="true"></i>Limpiar
      </button>
      <button type="button" class="kb-btn kb-btn-ghost kb-btn-icon"
              (click)="toggleHelp('filtros')" title="Ayuda sobre los filtros">
        <i class="fas fa-question-circle" aria-hidden="true"></i>
      </button>

      <span class="kb-toolbar-conteo whitespace-nowrap">
        {{ filteredPlanes.length }} de {{ plans.length }}
      </span>
    </div>

    <!-- ── Fila 2: los que se agregaron. Cada uno se puede sacar. ───────── -->
    <div *ngIf="filtrosPuestos.length > 0" class="flex w-full flex-wrap items-end gap-3">
      <div *ngFor="let clave of filtrosPuestos" class="min-w-[200px]">
        <label class="kb-label flex items-center justify-between gap-2">
          {{ etiquetaDelFiltro(clave) }}
          <button type="button" (click)="quitarFiltro(clave)"
                  class="rounded p-0.5 text-muted-foreground transition-colors hover:bg-accent hover:text-destructive"
                  [title]="'Quitar el filtro de ' + etiquetaDelFiltro(clave)">
            <i class="fas fa-times text-xs" aria-hidden="true"></i>
          </button>
        </label>

        <!-- Comercio -->
        <div class="kb-select-envoltorio" *ngIf="clave === 'comercio'">
          <select class="kb-select" [(ngModel)]="filters.comercio" (change)="onFilterChange()">
            <option value="">Todos los planes</option>
            <option value="ACOTADOS">Sólo los acotados a algún comercio</option>
            <option value="TODOS">Sólo los que aplican a todos</option>
            <option *ngFor="let c of comercios" [value]="c.id">Le aplica a {{ c.name }}</option>
          </select>
          <i class="fas fa-chevron-down kb-select-flecha" aria-hidden="true"></i>
        </div>

        <!-- Fecha de creación -->
        <div class="flex items-center gap-2" *ngIf="clave === 'fecha'">
          <div class="kb-select-envoltorio w-[170px]">
            <select class="kb-select" [(ngModel)]="filters.fecha" (change)="onFilterChange()">
              <option value="">Cualquier fecha</option>
              <option value="hoy">Hoy</option>
              <option value="7d">Últimos 7 días</option>
              <option value="30d">Últimos 30 días</option>
              <option value="anio">Este año</option>
              <option value="rango">Entre dos fechas…</option>
            </select>
            <i class="fas fa-chevron-down kb-select-flecha" aria-hidden="true"></i>
          </div>
          <input type="date" class="kb-input w-[150px]" *ngIf="filters.fecha === 'rango'"
                 [(ngModel)]="filters.fechaDesde" (change)="onFilterChange()" aria-label="Desde">
          <input type="date" class="kb-input w-[150px]" *ngIf="filters.fecha === 'rango'"
                 [(ngModel)]="filters.fechaHasta" (change)="onFilterChange()" aria-label="Hasta">
        </div>

        <!-- Vigencia -->
        <div class="kb-select-envoltorio" *ngIf="clave === 'vigencia'">
          <select class="kb-select" [(ngModel)]="filters.vigencia" (change)="onFilterChange()">
            <option value="">Cualquiera</option>
            <option value="vigentes">Vigentes</option>
            <option value="vencen30">Vencen en 30 días</option>
            <option value="vencidos">Vencidos</option>
            <option value="sin_fin">Sin vencimiento</option>
          </select>
          <i class="fas fa-chevron-down kb-select-flecha" aria-hidden="true"></i>
        </div>

        <!-- Producto -->
        <div class="kb-select-envoltorio" *ngIf="clave === 'producto'">
          <select class="kb-select" [(ngModel)]="filters.producto" (change)="onFilterChange()">
            <option value="">Todos los productos</option>
            <option *ngFor="let pr of productArray" [value]="pr?.params?.code">
              {{ pr?.params?.code }}
            </option>
          </select>
          <i class="fas fa-chevron-down kb-select-flecha" aria-hidden="true"></i>
        </div>

        <!-- Tienda -->
        <div class="kb-select-envoltorio" *ngIf="clave === 'tienda'">
          <select class="kb-select" [(ngModel)]="filters.tienda" (change)="onFilterChange()">
            <option value="">Cualquiera</option>
            <option value="acotados">Acotados a alguna tienda</option>
            <option value="todas">Sin acotar por tienda</option>
          </select>
          <i class="fas fa-chevron-down kb-select-flecha" aria-hidden="true"></i>
        </div>

        <!-- Etiquetas -->
        <div class="kb-select-envoltorio" *ngIf="clave === 'etiquetas'">
          <select class="kb-select" [(ngModel)]="filters.etiquetas" (change)="onFilterChange()">
            <option value="">Cualquiera</option>
            <option value="con">Con etiquetas</option>
            <option value="sin">Sin etiquetas</option>
          </select>
          <i class="fas fa-chevron-down kb-select-flecha" aria-hidden="true"></i>
        </div>
      </div>
    </div>
  </div>

  <div class="kb-aviso kb-aviso-info" *ngIf="showHelp['filtros']">
    <i class="fas fa-info-circle mt-0.5" aria-hidden="true"></i>
    <div class="flex-1">
      <p class="kb-aviso-titulo">{{ helpData['filtros']?.title }}</p>
      <p>{{ helpData['filtros']?.description }}</p>
      <div class="mt-3" *ngFor="let section of helpData['filtros']?.sections">
        <p class="font-medium">{{ section.title }}</p>
        <ul class="mt-1 ml-4 list-disc space-y-1">
          <li *ngFor="let item of section.items">
            <b>{{ item.label }}:</b> {{ item.description }}
          </li>
        </ul>
      </div>
    </div>
  </div>

  <!-- ══ Cargando ═══════════════════════════════════════════════════════════ -->
  <div class="kb-card" *ngIf="plansLoading">
    <div class="kb-card-body space-y-3">
      <div class="kb-skeleton h-10 w-full"></div>
      <div class="kb-skeleton h-10 w-full"></div>
      <div class="kb-skeleton h-10 w-full"></div>
      <div class="kb-skeleton h-10 w-full"></div>
    </div>
  </div>

  <!-- ══ Vacío ══════════════════════════════════════════════════════════════ -->
  <div class="kb-empty" *ngIf="!plansLoading && filteredPlanes.length === 0">
    <i class="fas fa-layer-group kb-empty-icono" aria-hidden="true"></i>
    <p class="kb-empty-titulo">No hay planes que coincidan</p>
    <p class="kb-empty-texto">
      Probá con otros filtros, o limpialos para ver los {{ plans.length }} planes cargados.
    </p>
    <button type="button" class="kb-btn kb-btn-outline kb-btn-sm mt-4" (click)="clearFilters()">
      Limpiar los filtros
    </button>
  </div>

  <!-- ══ La tabla ═══════════════════════════════════════════════════════════ -->
  <div class="kb-table-envoltorio" *ngIf="!plansLoading && filteredPlanes.length > 0">
    <!-- 🔴 Los anchos se declaran. Sin esto el navegador los reparte por el
         largo del CONTENIDO, y el resultado medido era al revés de lo que
         importa: «Última modificación» se llevaba el 21% —más que «Plan», que es
         el dato principal— y «Cuotas» y «Prioridad» gastaban 307 px entre las
         dos para mostrar números de uno o dos dígitos. «Prioridad» terminó
         oculta: un dígito no justifica una columna. -->
    <table class="kb-table">
      <colgroup>
        <col style="width: 26%">   <!-- Plan: el dato principal, el más ancho -->
        <col style="width: 11%">   <!-- Tipo: crédito / contado / suscripción -->
        <col style="width: 13%">   <!-- Comercios a los que aplica -->
        <col style="width: 13%">   <!-- Vigencia -->
        <col style="width: 7%">    <!-- Cuotas: un número -->
        <!-- Prioridad: columna OCULTA. Su 9% se repartió entre Plan (+7) y
             Estado y Acciones (+1 cada una). El dato sigue en el detalle del
             plan y el filtro de arriba lo sigue usando. -->
        <col style="width: 9%">    <!-- Estado: una insignia -->
        <col style="width: 12%">   <!-- Creado -->
        <!-- 🔴 «Última modificación» SE FUE. Se llevaba el 16% del ancho para un
             dato que casi nunca decide nada en un listado; sigue en el detalle
             del plan, que es donde se lo va a buscar. El espacio se repartió
             entre Tipo, Comercios y Vigencia, que antes no se veían. -->
        <col style="width: 9%">    <!-- Acciones -->
      </colgroup>
      <thead class="kb-thead">
        <tr>
          <!-- 📍 El orden vive en la cabecera, no en una barra aparte: es donde
               todo el mundo lo busca, y así la flecha está sobre la columna que
               efectivamente ordena. Misma función `sortBy` de antes. -->
          <th class="kb-th kb-th-orden" (click)="sortBy('name')">
            Plan
            <i class="fas" *ngIf="sortField === 'name'"
               [class.fa-arrow-up]="sortDirection === 'asc'"
               [class.fa-arrow-down]="sortDirection === 'desc'" aria-hidden="true"></i>
          </th>
          <th class="kb-th kb-th-orden" (click)="sortBy('type')">
            Tipo
            <i class="fas" *ngIf="sortField === 'type'"
               [class.fa-arrow-up]="sortDirection === 'asc'"
               [class.fa-arrow-down]="sortDirection === 'desc'" aria-hidden="true"></i>
          </th>
          <th class="kb-th">Comercios</th>
          <th class="kb-th">Vigencia</th>
          <th class="kb-th kb-th-num">Cuotas</th>
          <!-- Prioridad: OCULTA porque ocupaba ancho para mostrar un dígito.
               El orden por prioridad sigue existiendo en `sortBy('priority')` y
               el filtro de la barra de arriba sigue funcionando: se sacó la
               columna, no el dato.
          <th class="kb-th kb-th-orden kb-th-num" (click)="sortBy('priority')">
            Prioridad
            <i class="fas" *ngIf="sortField === 'priority'"
               [class.fa-arrow-up]="sortDirection === 'asc'"
               [class.fa-arrow-down]="sortDirection === 'desc'" aria-hidden="true"></i>
          </th>
          -->
          <th class="kb-th kb-th-orden" (click)="sortBy('status')">
            Estado
            <i class="fas" *ngIf="sortField === 'status'"
               [class.fa-arrow-up]="sortDirection === 'asc'"
               [class.fa-arrow-down]="sortDirection === 'desc'" aria-hidden="true"></i>
          </th>
          <th class="kb-th kb-th-orden" (click)="sortBy('date')">
            Creado
            <i class="fas" *ngIf="sortField === 'date'"
               [class.fa-arrow-up]="sortDirection === 'asc'"
               [class.fa-arrow-down]="sortDirection === 'desc'" aria-hidden="true"></i>
          </th>
          <th class="kb-th kb-th-acciones">Acciones</th>
        </tr>
      </thead>
      <tbody>
        <tr class="kb-tr kb-tr-clic" *ngFor="let plan of paginatedPlanes; trackBy: trackByPlanId"
            [ngClass]="{'kb-tr-atenuada': !plan.status}">
          <!-- Plan -->
          <td class="kb-td" (click)="verPlan(plan)">
            <p class="kb-td-titulo">{{ plan.name || plan.code }}</p>
            <p class="kb-td-sub">{{ plan.code }}</p>
          </td>

          <!-- Tipo: es el eje por el que se filtra, así que tiene que verse en
               la fila. Filtrar por «Suscripción» y después no poder distinguir
               qué es cada cosa es dejar el trabajo a medias. -->
          <td class="kb-td">
            <span class="kb-badge kb-badge-contorno">{{ etiquetaTipo(plan) }}</span>
          </td>

          <!-- Comercios: «Todos» NO es un vacío, es la respuesta. Un plan sin
               lista aplica a todos los comercios, y así se dice. -->
          <td class="kb-td">
            <span *ngIf="comerciosDelPlan(plan).length === 0" class="text-muted-foreground">
              Todos
            </span>
            <span *ngIf="comerciosDelPlan(plan).length === 1" class="kb-badge kb-badge-marca">
              {{ nombreComercio(comerciosDelPlan(plan)[0]) }}
            </span>
            <span *ngIf="comerciosDelPlan(plan).length > 1" class="kb-badge kb-badge-marca"
                  [title]="'Acotado a ' + comerciosDelPlan(plan).length + ' comercios'">
              {{ comerciosDelPlan(plan).length }} comercios
            </span>
          </td>

          <!-- Vigencia: 34 de 57 planes tienen fecha de fin y no se veía en
               ningún lado; el que no la tiene no está «incompleto», no vence. -->
          <td class="kb-td">
            <span *ngIf="!plan.params?.end_date" class="text-muted-foreground">Sin vencimiento</span>
            <span *ngIf="plan.params?.end_date">hasta {{ formatDate(plan.params.end_date) }}</span>
          </td>

          <td class="kb-td kb-td-num">{{ getQuotasText(plan) }}</td>
          <!-- <td class="kb-td kb-td-num">{{ getPriorityText(plan) }}</td> -->

          <!-- 🔴 El estado en PALABRAS, no solo en el color del interruptor.
               Antes «activo» e «inactivo» se distinguían nada más por el verde y
               el rojo de un icono: quien no distingue bien los colores no tenía
               forma de saberlo, y con la fila atenuada tampoco alcanzaba. -->
          <td class="kb-td">
            <span class="kb-badge" [ngClass]="plan.status ? 'kb-badge-exito' : 'kb-badge-neutro'">
              {{ plan.status ? 'Activo' : 'Inactivo' }}
            </span>
          </td>

          <!-- 🔴 El renglón de abajo sólo aparece si hay un nombre de verdad.
               Antes salía siempre, y como estas dos funciones devuelven «-»
               cuando no saben quién fue, debajo de CADA fecha había un guión
               suelto: 27 guiones que no dicen nada. Y en «Última modificación»
               llegaban a ser dos marcas de vacío pegadas, «—» y «-». -->
          <td class="kb-td">
            <p>{{ formatDate(plan?.date) }}</p>
            <p class="kb-td-sub" *ngIf="getCreatedByDisplayName(plan) !== '-'">
              {{ getCreatedByDisplayName(plan) }}
            </p>
          </td>

          <!-- Acciones visibles en la fila, no en un menú: son cuatro, y un
               desplegable acá pelearía z-index con Bootstrap sin ganar nada. -->
          <td class="kb-td kb-td-acciones">
            <div class="kb-acciones-grupo">
              <button type="button" class="kb-btn kb-btn-ghost kb-btn-icon"
                      (click)="verPlan(plan)" title="Ver el detalle">
                <i class="fas fa-eye" aria-hidden="true"></i>
              </button>
              <button type="button" class="kb-btn kb-btn-ghost kb-btn-icon"
                      *ngIf="authService.hasPermission('config.planes.create')"
                      (click)="duplicarPlan(plan)" title="Duplicar este plan">
                <i class="fas fa-copy" aria-hidden="true"></i>
              </button>
              <button type="button" class="kb-btn kb-btn-ghost kb-btn-icon"
                      *ngIf="authService.hasPermission('config.planes.update')"
                      (click)="editarPlan(plan)" title="Modificar">
                <i class="fas fa-edit" aria-hidden="true"></i>
              </button>
              <button type="button" class="kb-btn kb-btn-ghost kb-btn-icon"
                      *ngIf="authService.hasPermission('config.planes.update')"
                      (click)="toggleEstado(plan)"
                      [title]="plan.status ? 'Dejar de ofrecer este plan' : 'Volver a ofrecer este plan'">
                <i class="fas"
                   [class.fa-toggle-on]="plan.status"
                   [class.fa-toggle-off]="!plan.status" aria-hidden="true"></i>
              </button>
              <!-- 📍 Borrar solo si el plan no tiene créditos. La condición ya
                   estaba y se conserva: un plan con créditos otorgados no se
                   borra, se desactiva. -->
              <button type="button" class="kb-btn kb-btn-ghost kb-btn-icon"
                      *ngIf="(plan.invoices || 0) === 0 && authService.hasPermission('config.planes.delete')"
                      (click)="borrarPlan(plan)" title="Borrar plan">
                <i class="fas fa-trash text-destructive" aria-hidden="true"></i>
              </button>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <!-- ══ Paginación ═════════════════════════════════════════════════════════ -->
  <div class="kb-pager" *ngIf="!plansLoading && filteredPlanes.length > 0">
    <span class="kb-toolbar-conteo whitespace-nowrap">Página {{ currentPage }} de {{ totalPages }}</span>
    <div class="kb-acciones-grupo">
      <button type="button" class="kb-btn kb-btn-outline kb-btn-icon"
              (click)="previousPage()" [disabled]="currentPage === 1" title="Anterior">
        <i class="fas fa-chevron-left" aria-hidden="true"></i>
      </button>
      <button type="button" class="kb-btn kb-btn-sm"
              *ngFor="let page of visiblePages"
              [ngClass]="page === currentPage ? 'kb-btn-primary' : 'kb-btn-outline'"
              (click)="goToPage(page)">
        {{ page }}
      </button>
      <button type="button" class="kb-btn kb-btn-outline kb-btn-icon"
              (click)="nextPage()" [disabled]="currentPage === totalPages" title="Siguiente">
        <i class="fas fa-chevron-right" aria-hidden="true"></i>
      </button>
    </div>
  </div>

  <!-- Ayuda de las acciones. Va al final, junto a lo que explica. -->
  <div class="kb-aviso kb-aviso-info" *ngIf="showHelp['acciones']">
    <i class="fas fa-info-circle mt-0.5" aria-hidden="true"></i>
    <div class="flex-1">
      <p class="kb-aviso-titulo">{{ helpData['acciones']?.title }}</p>
      <p>{{ helpData['acciones']?.description }}</p>
      <div class="mt-3" *ngFor="let section of helpData['acciones']?.sections">
        <p class="font-medium">{{ section.title }}</p>
        <ul class="mt-1 ml-4 list-disc space-y-1">
          <li *ngFor="let item of section.items">
            <b>{{ item.label }}:</b> {{ item.description }}
          </li>
        </ul>
      </div>
    </div>
  </div>

  <p class="text-xs text-muted-foreground">
    Un plan inactivo deja de ofrecerse a nuevos clientes.
    <b>Los créditos ya otorgados con ese plan siguen su curso</b> y se cobran igual.
    <button type="button" class="kb-btn kb-btn-ghost kb-btn-sm" (click)="toggleHelp('acciones')">
      Ver qué hace cada acción
    </button>
  </p>
</div>
